import { useEffect, useState } from "react";
import { theme, btnPrimary, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { dataCategoryLabel } from "../lib/dataCategories";
import { DataCategoryPicker } from "./DataCategoryPicker";
import { textarea, secondaryBtn } from "../pages/resilience/shared";

// Shadow AI onboarding in one step (backend lib/shadowAiOnboarding.ts, POST /ai-systems/:id/onboard):
// a detected tool with no supplier becomes a prospective supplier, a confirmed and linked register
// entry and a DPIA check, on the member's click. The plan is read from the server before the click,
// so what the page says it will do is what the server will do.

type PersonalData = "yes" | "no" | "unknown";

interface Plan {
  supplier: { action: "link"; supplierId: string; name: string } | { action: "create"; name: string; riskTier: string; isDataProcessor: boolean };
  dpia: { action: "draft" | "attention" | "none"; level: string; reasons: string[] };
  system: { dataCategories: string[] };
}
interface Preview { plan: Plan; tprmEnabled: boolean; dataProtectionModuleOpen: boolean; missingPermissions: string[] }

const PERMISSION_WORDS: Record<string, string> = {
  "risks:write": "change the AI register",
  "tprm:write": "change the supplier register",
  "privacy:write": "change the data protection registers",
};

const ANSWERS: Array<{ id: PersonalData; label: string }> = [
  { id: "unknown", label: "Not known yet" },
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

export function ShadowAiOnboardingPanel({ systemId, systemName, onDone }: { systemId: string; systemName: string; onDone: (message: string) => void }) {
  const [personalData, setPersonalData] = useState<PersonalData>("unknown");
  const [categories, setCategories] = useState<string[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    const q = new URLSearchParams({ personalData, ...(categories.length ? { dataCategories: categories.join(",") } : {}) });
    api.get<Preview>(`/ai-systems/${systemId}/onboarding?${q.toString()}`)
      .then((p) => { if (live) { setPreview(p); setError(""); } })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Could not work out what onboarding would do.")); });
    return () => { live = false; };
  }, [systemId, personalData, categories]);

  async function onboard() {
    setBusy(true); setError("");
    try {
      const { result } = await api.post<{ result: { supplier: { name: string; created: boolean }; dpia: { action: string } } }>(
        `/ai-systems/${systemId}/onboard`, { personalData, dataCategories: categories },
      );
      onDone(`${systemName} onboarded: ${result.supplier.name} ${result.supplier.created ? "added to the supplier register as prospective" : "linked from the supplier register"}${
        result.dpia.action === "draft" ? ", draft DPIA started" : result.dpia.action === "attention" ? ", DPIA check raised" : ""}.`);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not onboard the system."));
    } finally { setBusy(false); }
  }

  const plan = preview?.plan;
  const blockedBy = preview
    ? [
      ...preview.missingPermissions.map((p) => `your role does not allow you to ${PERMISSION_WORDS[p] ?? p}`),
      ...(!preview.tprmEnabled && plan?.supplier.action === "create" ? ["the supplier register (TPRM add-on) is not switched on"] : []),
    ]
    : [];

  return (
    <div>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        No supplier in your register matches this vendor. Onboarding adds the vendor as a prospective supplier, confirms this
        entry and links the two, and raises the DPIA check, in one step. The supplier stays prospective until it is approved
        from its panel.
      </div>

      <div style={{ ...text.label, marginBottom: 4 }}>Does personal data go into it?</div>
      <div role="radiogroup" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        {ANSWERS.map((a) => (
          <label key={a.id} style={{ ...text.body, display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="radio" name={`pd-${systemId}`} checked={personalData === a.id} onChange={() => setPersonalData(a.id)} />
            {a.label}
          </label>
        ))}
      </div>

      {personalData === "yes" && (
        <>
          <div style={{ ...text.label, marginBottom: 4 }}>Which kinds (optional)</div>
          <div style={{ marginBottom: 10 }}>
            <DataCategoryPicker selected={categories} onChange={setCategories} />
          </div>
        </>
      )}

      {plan && (
        <div style={{ border: `1px solid ${theme.color.line}`, borderRadius: 3, padding: "8px 12px", marginBottom: 10 }}>
          <div style={{ ...text.label, marginBottom: 4 }}>What this will do</div>
          <ul style={{ ...text.body, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              {plan.supplier.action === "create"
                ? <>Add <strong>{plan.supplier.name}</strong> to the supplier register as prospective, {plan.supplier.riskTier} risk tier{plan.supplier.isDataProcessor ? ", recorded as a data processor" : ""}.</>
                : <>Link the supplier you already hold, <strong>{plan.supplier.name}</strong>.</>}
            </li>
            <li>Confirm this entry as in use{plan.system.dataCategories.length ? `, handling ${plan.system.dataCategories.map((c) => dataCategoryLabel(c).toLowerCase()).join(", ")} data` : ""}.</li>
            <li>
              {plan.dpia.action === "draft" && <>Start a draft DPIA, linked to the system and the supplier: {plan.dpia.reasons.join("; ")}.</>}
              {plan.dpia.action === "attention" && <>Raise a DPIA check on this register until someone records the outcome: {plan.dpia.reasons.join("; ")}.</>}
              {plan.dpia.action === "none" && <>No DPIA check: {plan.dpia.reasons.join(" ")}</>}
            </li>
          </ul>
        </div>
      )}

      {blockedBy.length > 0 && (
        <div style={{ ...text.hint, color: theme.color.warningText, marginBottom: 8 }}>
          Cannot onboard from here: {blockedBy.join("; ")}. Ask an administrator if you need to.
        </div>
      )}
      {error && <div style={{ ...text.hint, color: theme.color.danger, marginBottom: 8 }}>{error}</div>}

      <button
        onClick={onboard}
        disabled={busy || !preview || blockedBy.length > 0}
        style={{ ...btnPrimary, opacity: busy || !preview || blockedBy.length > 0 ? 0.6 : 1 }}
      >
        {busy ? "Onboarding…" : "Onboard in one step"}
      </button>
    </div>
  );
}

/** The DPIA check onboarding raised, answered where no DPIA is linked (the organisation has no data protection module, or keeps it elsewhere). */
export function DpiaCheckOutcome({ systemId, reasons, onDone }: { systemId: string; reasons: string[]; onDone: (message: string) => void }) {
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setBusy(true); setError("");
    try {
      await api.post(`/ai-systems/${systemId}/dpia-check`, { outcome });
      onDone("DPIA check outcome recorded.");
    } catch (e) { setError(apiErrorMessage(e, "Could not record the outcome.")); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <div style={{ ...text.hint, marginBottom: 8 }}>
        Raised when it was onboarded: {reasons.join("; ") || "a new AI tool in use"}. Carry out a DPIA and link it from the entry, or record
        why one is not needed.
      </div>
      <textarea
        value={outcome}
        onChange={(e) => setOutcome(e.target.value)}
        placeholder="Why no DPIA is needed, or where the assessment is kept"
        style={{ ...textarea, minHeight: 64 }}
      />
      {error && <div style={{ ...text.hint, color: theme.color.danger, margin: "6px 0" }}>{error}</div>}
      <button onClick={save} disabled={busy || outcome.trim().length < 20} style={{ ...secondaryBtn, marginTop: 8, opacity: busy || outcome.trim().length < 20 ? 0.6 : 1 }}>
        Record the outcome
      </button>
    </div>
  );
}
